import { forwardRef, useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: string | number | null;
  onValueChange?: (value: string) => void;
  currency?: string;
  "data-testid"?: string;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onValueChange, currency: _currency = "AUD", className, "data-testid": testId, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const isUserInputRef = useRef(false);

    // Format number to currency display
    const formatCurrency = (val: string | number | null | undefined): string => {
      if (val === null || val === undefined || val === "") return "";
      
      const numericValue = typeof val === "string" ? parseFloat(val) : val;
      if (isNaN(numericValue)) return "";
      
      return numericValue.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // Remove formatting to get numeric value
    const unformatCurrency = (val: string): string => {
      return val.replace(/[^0-9.]/g, "");
    };

    // Update display value when prop value changes (but not during user input)
    useEffect(() => {
      // Don't format while user is actively typing
      if (isUserInputRef.current) {
        isUserInputRef.current = false;
        return;
      }
      
      // Don't format while focused (user is editing)
      if (isFocused) {
        if (value !== undefined && value !== null) {
          const unformatted = typeof value === "string" ? value : value.toString();
          setDisplayValue(unformatted);
        } else {
          setDisplayValue("");
        }
        return;
      }
      
      // Format the value when not focused
      if (value !== undefined && value !== null && value !== "") {
        setDisplayValue(formatCurrency(value));
      } else {
        setDisplayValue("");
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      // Allow only numbers, decimal point, and comma
      const cleaned = inputValue.replace(/[^0-9.,]/g, "");
      const unformatted = unformatCurrency(cleaned);
      
      // Validate decimal places (max 2)
      const parts = unformatted.split(".");
      if (parts.length > 2) return; // Multiple decimal points
      if (parts[1] && parts[1].length > 2) return; // More than 2 decimal places
      
      // Mark this as user input to prevent useEffect from overwriting
      isUserInputRef.current = true;
      setDisplayValue(cleaned);
      onValueChange?.(unformatted);
    };

    const handleBlur = () => {
      setIsFocused(false);
      // Format on blur
      const unformatted = unformatCurrency(displayValue);
      if (unformatted) {
        setDisplayValue(formatCurrency(unformatted));
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      // Unformat on focus for easier editing
      const unformatted = unformatCurrency(displayValue);
      if (unformatted) {
        setDisplayValue(unformatted);
      }
    };

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          $
        </span>
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className={`pl-7 ${className || ""}`}
          data-testid={testId}
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
